import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import {
  useAddOption,
  useCreateCustomField,
  useCustomFields,
  useDeleteCustomField,
  useUpdateCustomField,
  useUpdateOption,
} from '../api/hooks';
import type { CustomField, FieldType } from '../api/types';
import { EmptyState, ErrorBanner, Modal } from '../components/ui';

const FIELD_TYPES: Array<{ value: FieldType; label: string; hasOptions?: boolean }> = [
  { value: 'text', label: 'Text' },
  { value: 'long_text', label: 'Long text' },
  { value: 'number', label: 'Number' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'url', label: 'URL' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date & time' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'select', label: 'Dropdown', hasOptions: true },
  { value: 'radio', label: 'Radio buttons', hasOptions: true },
  { value: 'multi_select', label: 'Multi-select', hasOptions: true },
];

const OPTION_TYPES = new Set(['select', 'radio', 'multi_select']);

/** Mirrors the server's derivation, so the admin sees the key before saving. */
function previewApiKey(label: string): string {
  const slug = label
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[₹]/g, ' rupees ')
    .replace(/[%]/g, ' percent ')
    .replace(/[&]/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!slug) return 'cf_field';
  return `cf_${/^[a-z]/.test(slug) ? slug : `f_${slug}`}`.slice(0, 62);
}

export function CustomFieldsPage() {
  const { data: fields = [], isPending } = useCustomFields(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CustomField | null>(null);
  const [error, setError] = useState<unknown>(null);

  const updateField = useUpdateCustomField();
  const deleteField = useDeleteCustomField();

  async function toggleActive(field: CustomField) {
    setError(null);
    try {
      await updateField.mutateAsync({ id: field.id, is_active: !field.is_active });
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Custom fields</h1>
          <p className="sub">
            Fields you add here appear on every lead form, filter and export — no developer needed.
          </p>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>+ New field</button>
        </div>
      </div>

      <ErrorBanner error={error} />

      <div className="banner banner-info">
        <div>
          The <strong>API key</strong> is generated once from the label and then frozen. Renaming a
          field is always safe — integrations keep working, and existing lead data is untouched.
        </div>
      </div>

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Label</th>
              <th>API key</th>
              <th>Type</th>
              <th>Options</th>
              <th>Required</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => (
              <tr key={field.id} style={field.is_active ? undefined : { opacity: 0.55 }}>
                <td><strong>{field.label}</strong></td>
                <td className="mono" style={{ color: 'var(--accent)' }}>{field.api_key}</td>
                <td>{FIELD_TYPES.find((t) => t.value === field.field_type)?.label ?? field.field_type}</td>
                <td className="truncate">
                  {field.options.length > 0
                    ? field.options.filter((o) => o.is_active).map((o) => o.label).join(', ')
                    : '—'}
                </td>
                <td>{field.is_required ? 'Yes' : 'No'}</td>
                <td>
                  <span className="chip chip-plain">{field.is_active ? 'Active' : 'Inactive'}</span>
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditing(field)}>Edit</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => toggleActive(field)}>
                    {field.is_active ? 'Deactivate' : 'Reactivate'}
                  </button>
                  {field.is_active && (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ color: 'var(--danger)' }}
                      onClick={async () => {
                        if (!confirm(`Delete "${field.label}"?\n\nExisting values are kept and the key ${field.api_key} stays reserved forever.`)) return;
                        try {
                          await deleteField.mutateAsync(field.id);
                        } catch (err) {
                          setError(err);
                        }
                      }}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!isPending && fields.length === 0 && (
          <EmptyState
            title="No custom fields yet"
            action={<button className="btn btn-primary" onClick={() => setCreating(true)}>Create the first one</button>}
          >
            Custom fields capture whatever your business needs — school name, budget, preferred
            batch — without a schema change.
          </EmptyState>
        )}
      </div>

      {creating && <CreateFieldModal onClose={() => setCreating(false)} />}
      {editing && <EditFieldModal field={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function CreateFieldModal({ onClose }: { onClose: () => void }) {
  const create = useCreateCustomField();
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState<FieldType>('text');
  const [helpText, setHelpText] = useState('');
  const [required, setRequired] = useState(false);
  const [optionsText, setOptionsText] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const needsOptions = OPTION_TYPES.has(fieldType);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const options = optionsText
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => ({ label: l }));

    try {
      await create.mutateAsync({
        label: label.trim(),
        field_type: fieldType,
        help_text: helpText.trim() || null,
        is_required: required,
        ...(needsOptions ? { options } : {}),
      });
      onClose();
    } catch (err) {
      setError(err);
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors);
    }
  }

  return (
    <Modal
      title="New custom field"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">Cancel</button>
          <button className="btn btn-primary" form="new-field" disabled={create.isPending}>
            {create.isPending ? <span className="spinner" /> : 'Create field'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} />
      <form id="new-field" onSubmit={submit}>
        <div className={`field${fieldErrors.label ? ' invalid' : ''}`}>
          <label htmlFor="label">Field name<span className="req">*</span></label>
          <input id="label" required autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. School Name" />
          {/* Showing the derived key up front sets the expectation that it is
              permanent, before the admin has committed to a name. */}
          <div className="hint">
            API key: <code style={{ color: 'var(--accent)' }}>{label ? previewApiKey(label) : 'cf_…'}</code>
            {' — permanent once created'}
          </div>
        </div>

        <div className="field">
          <label htmlFor="type">Field type<span className="req">*</span></label>
          <select id="type" value={fieldType} onChange={(e) => setFieldType(e.target.value as FieldType)}>
            {FIELD_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <div className="hint">Cannot be changed later — values would be reinterpreted.</div>
        </div>

        {needsOptions && (
          <div className="field">
            <label htmlFor="options">Options<span className="req">*</span></label>
            <textarea
              id="options"
              required
              placeholder={'9th\n10th\n11th\n12th\nDropper'}
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
            />
            <div className="hint">One per line. You can rename these later without affecting saved leads.</div>
          </div>
        )}

        <div className="field">
          <label htmlFor="help">Help text</label>
          <input id="help" value={helpText} onChange={(e) => setHelpText(e.target.value)} placeholder="Shown under the input" />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
          Required when creating a lead
        </label>
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */

function EditFieldModal({ field, onClose }: { field: CustomField; onClose: () => void }) {
  const update = useUpdateCustomField();
  const addOption = useAddOption();
  const updateOption = useUpdateOption();

  const [label, setLabel] = useState(field.label);
  const [helpText, setHelpText] = useState(field.help_text ?? '');
  const [required, setRequired] = useState(field.is_required);
  const [newOption, setNewOption] = useState('');
  const [error, setError] = useState<unknown>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({
        id: field.id,
        label: label.trim(),
        help_text: helpText.trim() || null,
        is_required: required,
      });
      onClose();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <Modal
      title={`Edit "${field.label}"`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">Cancel</button>
          <button className="btn btn-primary" form="edit-field" disabled={update.isPending}>
            {update.isPending ? <span className="spinner" /> : 'Save'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} />
      <form id="edit-field" onSubmit={submit}>
        <div className="field">
          <label htmlFor="ef-label">Field name</label>
          <input id="ef-label" required value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>

        <div className="grid-2">
          <div className="field">
            <label>API key</label>
            {/* Disabled rather than hidden: admins need to know it for
                integrations, and the reason it is locked. */}
            <input value={field.api_key} disabled title="Permanent — integrations depend on it" />
            <div className="hint">Locked permanently</div>
          </div>
          <div className="field">
            <label>Type</label>
            <input value={FIELD_TYPES.find((t) => t.value === field.field_type)?.label ?? field.field_type} disabled />
            <div className="hint">Locked — would reinterpret stored values</div>
          </div>
        </div>

        <div className="field">
          <label htmlFor="ef-help">Help text</label>
          <input id="ef-help" value={helpText} onChange={(e) => setHelpText(e.target.value)} />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, marginBottom: 16 }}>
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
          Required when creating a lead
        </label>
      </form>

      {OPTION_TYPES.has(field.field_type) && (
        <>
          <h4 style={{ margin: '4px 0 8px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>
            Options
          </h4>
          <table className="data" style={{ marginBottom: 12 }}>
            <tbody>
              {field.options.map((option) => (
                <tr key={option.id}>
                  <td style={{ width: '55%' }}>{option.label}</td>
                  <td className="mono" style={{ color: 'var(--muted)' }}>{option.value}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() =>
                        updateOption.mutate({ fieldId: field.id, optionId: option.id, is_active: !option.is_active })
                      }
                    >
                      {option.is_active ? 'Retire' : 'Restore'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', gap: 8 }}>
            <input
              placeholder="Add an option…"
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newOption.trim()) {
                  e.preventDefault();
                  addOption.mutate({ fieldId: field.id, label: newOption.trim() });
                  setNewOption('');
                }
              }}
            />
            <button
              type="button"
              className="btn"
              disabled={!newOption.trim() || addOption.isPending}
              onClick={() => {
                addOption.mutate({ fieldId: field.id, label: newOption.trim() });
                setNewOption('');
              }}
            >
              Add
            </button>
          </div>
          <div className="hint" style={{ marginTop: 6 }}>
            Renaming an option is safe. Retiring one hides it from new leads but keeps it on leads
            that already have it.
          </div>
        </>
      )}
    </Modal>
  );
}
